import {
    type Dinero as DineroObject,
    USD,
    add,
    allocate,
    dinero,
    greaterThan,
    halfEven,
    multiply,
    toDecimal,
    toSnapshot,
} from "dinero.js";

import { IBasketLineDiscount } from "../models/catalogue";
import { IMonths, Months, isoMonths } from "../models/nominals";
import { IPostDiscountAddons, IPrice, IPriceQuantity } from "../models/prices";
import { getBestEligiblePlan } from "./financing";
import { decimal } from "./format";
import { anyTruthy, notEmpty } from "./functional";
import { isDinero } from "./guards";
import { getNumber } from "./numbers";

export type Dinero = DineroObject<number, "USD">;

export type IDineroConvertable = string | number | Dinero | null | undefined;

export const ZERO: Dinero = dinero({ amount: 0, currency: USD });

/**
 * Convert the given number to minor currency
 *
 * dinero.js requires the amount to be in minor currency (cents).
 * For now assumes USD is used.
 */
const getMinorCurrency = (value: number, multiplier = 100): number => {
    return value * multiplier;
};

/**
 * Convert the value to a type accepted by dinero.js
 */
const getDineroNum = (value: string | number | undefined | null): number => {
    const num = getNumber(value, 0);
    return Math.round(getMinorCurrency(num));
};

/**
 * Construct and return a dinero object using the parameter
 */
export const getDinero = (value: IDineroConvertable): Dinero => {
    if (isDinero(value)) {
        return value;
    }
    const amount = getDineroNum(value);
    return dinero({ amount: amount, currency: USD });
};

/**
 * The value in major units (dollars) as a plain number, for arithmetic that
 * leaves money-land (ratios, sort keys, analytics payloads).
 */
export const toUnit = (value: Dinero): number => {
    return Number(toDecimal(value));
};

/**
 * Divide an amount, rounding half-to-even. Use {@link getMonthlyPrice}
 * instead when the parts must add back up to the original amount.
 */
export const divide = (value: Dinero, divisor: number): Dinero => {
    const { amount, scale } = toSnapshot(value);
    return dinero({
        amount: halfEven(amount, divisor, value.calculator),
        currency: USD,
        scale,
    });
};

/**
 * Convert a positive amount to negative and vis-versa. Useful for showing a
 * discount amount as "-42", etc.
 */
export const invert = (value: IDineroConvertable): Dinero => {
    return multiply(getDinero(value), -1);
};

/**
 * Sum a list of Dinero objects.
 */
export const sum = (prices: readonly Dinero[]): Dinero => {
    return prices.reduce((memo, price) => add(memo, price), ZERO);
};

/**
 * Calculate a monthly financing price based on total principal and term length (always
 * assumes 0% interest).
 */
export const getMonthlyPrice = (
    totalPrice: Dinero,
    termLength: IMonths | number,
): Dinero => {
    const length = Months.is(termLength)
        ? isoMonths.unwrap(termLength)
        : termLength;
    if (length <= 0) {
        return totalPrice;
    }
    const ratios = new Array<number>(length).fill(1);
    const payments = allocate(totalPrice, ratios);
    const largestPayment = payments.reduce<Dinero>((memo, payment) => {
        return greaterThan(payment, memo) ? payment : memo;
    }, payments[0]);
    return largestPayment;
};

const sumPriceQuantityProperties = (
    priceQuantities: IPriceQuantity[],
    property:
        | "excl_tax"
        | "incl_tax"
        | "tax"
        | "retail"
        | "cosmetic_excl_tax"
        | "cosmetic_incl_tax",
): Dinero => {
    return priceQuantities.reduce<Dinero>((memo, [quantity, price]) => {
        return add(memo, multiply(getDinero(price[property]), quantity));
    }, ZERO);
};

export const sumPriceQuantities = (
    priceQuantities: IPriceQuantity[],
    updateTermLength = true,
): IPrice | null => {
    if (priceQuantities.length <= 0) {
        return null;
    }
    const price: IPrice = {
        ...priceQuantities[0][1],
        excl_tax: decimal(
            sumPriceQuantityProperties(priceQuantities, "excl_tax"),
        ),
        incl_tax: decimal(
            sumPriceQuantityProperties(priceQuantities, "incl_tax"),
        ),
        tax: decimal(sumPriceQuantityProperties(priceQuantities, "tax")),
        retail: decimal(sumPriceQuantityProperties(priceQuantities, "retail")),
        cosmetic_excl_tax: decimal(
            sumPriceQuantityProperties(priceQuantities, "cosmetic_excl_tax"),
        ),
        cosmetic_incl_tax: decimal(
            sumPriceQuantityProperties(priceQuantities, "cosmetic_incl_tax"),
        ),
        post_discount_addons: priceQuantities.reduce<IPostDiscountAddons>(
            (memo, [qty, prc]) => {
                const newAddons = (prc.post_discount_addons || []).map(
                    (addon) => ({
                        name: addon.name,
                        price_excl_tax: decimal(
                            multiply(getDinero(addon.price_excl_tax), qty),
                        ),
                    }),
                );
                return [...memo, ...newAddons];
            },
            [],
        ),
    };
    const perMonths = priceQuantities.map(
        ([_qty, p]) => p.enable_per_month_pricing && p.per_month_term_length,
    );
    const financingPrincipal = getDinero(price.cosmetic_excl_tax);
    const enablePerMonth = anyTruthy(perMonths);
    let termLength = isoMonths.wrap(
        Math.max(
            ...priceQuantities
                .map(([_qty, p]) => p.per_month_term_length)
                .filter(notEmpty)
                .map(isoMonths.unwrap),
        ),
    );
    if (updateTermLength) {
        const plan = getBestEligiblePlan(financingPrincipal);
        if (plan) {
            termLength = plan.length;
        }
    }
    if (price.cosmetic_excl_tax && enablePerMonth && termLength) {
        return {
            ...price,
            enable_per_month_pricing: true,
            per_month_term_length: termLength,
            per_month: decimal(getMonthlyPrice(financingPrincipal, termLength)),
        };
    }
    return price;
};

export const getTotalPriceWithAddons = (
    priceTotal: IPrice,
    selectedAddonPrice: IPrice | null,
): IPrice => {
    const prices: IPriceQuantity[] = [[1, priceTotal]];
    if (selectedAddonPrice) {
        prices.push([1, selectedAddonPrice]);
    }
    const totalPriceWithAddons = sumPriceQuantities(prices) || priceTotal;
    return totalPriceWithAddons;
};

export const getHighestDiscountName = (
    discounts: readonly IBasketLineDiscount[],
): string | null => {
    const highestDiscount = discounts.reduce((prev, current) => {
        return parseFloat(current.amount) > parseFloat(prev.amount)
            ? current
            : prev;
    }, discounts[0]);
    return highestDiscount
        ? highestDiscount.voucher_name || highestDiscount.offer_name
        : null;
};
